/* eslint-disable no-underscore-dangle */
/* eslint-disable */
import React, {
  useRef,
  useContext,
  useEffect,
  useCallback,
  useState,
} from "react";
import PropTypes from "prop-types";
import geoViewport from "@mapbox/geo-viewport";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import {
  area,
  centroid,
  envelope,
  featureCollection,
  length,
  lineString,
} from "@turf/turf";
import format from "date-fns/format";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import ReactSlider from "react-slider";
import { ShoppingCart } from "phosphor-react";
import { transformDataToMapboxSource } from "utils";
import CheckBox from "components/CheckBox";
// eslint-disable-next-line
import { UserContext } from "context/user";
import { MapActions, MapContext } from "context/map";
import CartModal from "components/Modal/CartModal";
// eslint-disable-next-line
import mapboxgl from "!mapbox-gl";
import MapCustomControl from "../MapCustomControl";
import Logo from "../Logo";
import HarvestMoisture from "../HarvestMoisture";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import "./index.scss";

mapboxgl.accessToken = process.env.REACT_APP_MAPGL_ACCESS_TOKEN;
const StaticMode = { ...MapboxDraw.modes.simple_select };

const Map = ({ lastSelectedSourceRef }) => {
  const mapContainer = useRef(null);
  const { token, profile } = useContext(UserContext);
  const { state, dispatch } = useContext(MapContext);
  const dateNow = format(new Date(), "dd MMM yyyy");
  const {
    map,
    farm,
    fields,
    selectedFields,
    drawerOpen,
    selectedDate,
    catalog,
    selectedAlgorithm,
    mode,
    rasterTiles,
    measureMode,
    draw,
    exportMode,
    zoneLayerTransparency,
    isToggled,
    selectedLayer,
  } = state;
  const linePopUpRef = useRef(null);
  const areaPopUpRef = useRef(null);
  const [zoneLayerTransparencyLabel, setZoneLayerTransparencyLabel] =
    useState(null);
  const [openCart, setOpenCart] = useState(false);
  const [toggleAlgorithm, setToggleAlgorithm] = useState(true);

  const getMapBounds = useCallback(() => {
    const mapElement = mapContainer.current.getBoundingClientRect();
    const mapDimension = [mapElement.height, mapElement.width];
    const bounds = geoViewport.viewport(farm.bbox, mapDimension, 0, 11, 512);

    return bounds;
  }, [farm?.bbox]);

  const clearPopUps = useCallback(() => {
    const popups = document.getElementsByClassName("mapboxgl-popup");
    if (linePopUpRef) {
      for (let i = 0; i < popups.length; i + 1) {
        popups[i].remove();
      }
    }
    if (areaPopUpRef) {
      for (let i = 0; i < popups.length; i + 1) {
        popups[i].remove();
      }
    }
  }, []);

  const measureMethod = useCallback(
    (e, newDraw, newMap) => {
      const isMeasuring = localStorage.getItem("isMeasuring");
      if (measureMode !== "" && isMeasuring === "1") {
        newDraw?.setFeatureProperty(e?.features[0]?.id, "portColor", "#f7901e");
        const data = newDraw.getAll();
        clearPopUps();
        if (data.features.length > 0) {
          data.features.forEach((element) => {
            if (element?.geometry.type === "LineString") {
              const { coordinates } = element.geometry;
              const position = coordinates[coordinates.length - 1];
              const line = lineString(coordinates);
              const result = length(line, "kilometers");
              const newLinePopUp = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
              });
              newLinePopUp
                .setLngLat(position)
                .setHTML(`<div>${result.toLocaleString()} (km)</div>`)
                .addTo(newMap);
              newLinePopUp._container.id = `pop-${element?.id}`;
              linePopUpRef.current = newLinePopUp;
            } else if (element?.geometry?.type === "Polygon") {
              const center = centroid(element).geometry.coordinates;
              const result = Math.round((area(element) / 1000) * 10) / 100;
              const newAreaPopupWindow = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
              });

              newAreaPopupWindow
                .setLngLat(center)
                .setHTML(`<div>${result.toLocaleString()} (Ha)</div>`)
                .addTo(newMap);
              newAreaPopupWindow._container.id = `pop-${element?.id}`;
              areaPopUpRef.current = newAreaPopupWindow;
            }
          });
        }
        dispatch({ type: MapActions.SET_MEASURE_MODE, value: "" });
      }
    },
    [dispatch, clearPopUps, measureMode]
  );

  const initializeControls = useCallback(
    (newMap) => {
      newMap.addControl(new Logo(), "bottom-right");
      newMap.addControl(new MapCustomControl(), "bottom-right");
      newMap.addControl(
        new mapboxgl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true,
          },
          trackUserLocation: true,
          showUserHeading: true,
        }),
        "bottom-right"
      );
      newMap.addControl(new mapboxgl.NavigationControl(), "bottom-right");
      newMap.addControl(new mapboxgl.ScaleControl());
      newMap.getBearing();

      // initialize draw
      const newDraw = new MapboxDraw({
        modes: {
          static: StaticMode,
          ...MapboxDraw.modes,
        },
        displayControlsDefault: false,
        defaultMode: "static",
        userProperties: true,
        styles: [
          {
            id: "gl-draw-polygon-fill",
            type: "fill",
            filter: [
              "all",
              ["==", "$type", "Polygon"],
              ["!=", "mode", "static"],
            ],
            paint: {
              "fill-color": "#ffffff",
              "fill-opacity": 0.2,
            },
          },
          {
            id: "gl-draw-polygon-midpoint",
            type: "circle",
            filter: [
              "all",
              ["==", "$type", "Point"],
              ["==", "meta", "midpoint"],
            ],
            paint: {
              "circle-radius": 5,
              "circle-color": "#ffffff",
            },
          },
          {
            id: "gl-draw-polygon-stroke-active",
            type: "line",
            filter: [
              "all",
              ["==", "$type", "Polygon"],
              ["!=", "mode", "static"],
            ],
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
            paint: {
              "line-color": [
                "case",
                ["==", ["get", "user_portColor"], "#f7901e"],
                "#f7901e",
                ["==", ["get", "active"], "true"],
                "#09BA51",
                ["==", ["get", "active"], "false"],
                "#ffffff",
                "#ffffff",
              ],
              "line-width": 3,
              "line-dasharray": [
                "match",
                ["get", "user_portColor"],
                "#f7901e",
                ["literal", [0.2, 2]],
                ["literal", [1]],
              ],
            },
          },
          {
            id: "gl-draw-polygon-stroke-static",
            type: "line",
            filter: [
              "all",
              ["==", "$type", "Polygon"],
              ["==", "mode", "static"],
            ],
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
            paint: {
              "line-color": [
                "case",
                ["==", ["get", "user_portColor"], "#f7901e"],
                "#f7901e",
                ["==", ["get", "active"], "true"],
                "#09BA51",
                ["==", ["get", "active"], "false"],
                "#ffffff",
                "#ffffff",
              ],
              "line-width": 3,
              "line-dasharray": [
                "match",
                ["get", "user_portColor"],
                "#f7901e",
                ["literal", [0.2, 2]],
                ["literal", [1]],
              ],
            },
          },
          {
            id: "gl-draw-polygon-and-line-vertex-halo-active",
            type: "circle",
            filter: [
              "all",
              ["==", "meta", "vertex"],
              ["==", "$type", "Point"],
              ["!=", "mode", "static"],
            ],
            paint: {
              "circle-radius": 10,
              "circle-color": "#ffffff",
              "circle-opacity": 0.5,
            },
          },
          {
            id: "gl-draw-polygon-and-line-vertex-active",
            type: "circle",
            filter: [
              "all",
              ["==", "meta", "vertex"],
              ["==", "$type", "Point"],
              ["!=", "mode", "static"],
            ],
            paint: {
              "circle-radius": 4,
              "circle-color": "#fff",
            },
          },
          // lineString
          {
            id: "gl-draw-line-static",
            type: "line",
            filter: [
              "all",
              ["==", "mode", "static"],
              ["==", "$type", "LineString"],
            ],
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
            paint: {
              "line-color": [
                "case",
                ["==", ["get", "user_portColor"], "#f7901e"],
                "#f7901e",
                ["==", ["get", "active"], "true"],
                "#09BA51",
                ["==", ["get", "active"], "false"],
                "#ffffff",
                "#ffffff",
              ],
              "line-width": 3,
              "line-dasharray": [
                "match",
                ["get", "user_portColor"],
                "#f7901e",
                ["literal", [0.2, 2]],
                ["literal", [1]],
              ],
            },
          },
          {
            id: "gl-draw-point-static",
            type: "circle",
            filter: ["all", ["==", "mode", "static"], ["==", "$type", "Point"]],
            paint: {
              "circle-radius": 5,
              "circle-color": "#404040",
            },
          },
          {
            id: "gl-draw-line-inactive",
            type: "line",
            filter: [
              "all",
              ["==", "active", "false"],
              ["==", "$type", "LineString"],
              ["!=", "mode", "static"],
            ],
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
            paint: {
              "line-color": [
                "case",
                ["==", ["get", "user_portColor"], "#f7901e"],
                "#f7901e",
                ["==", ["get", "active"], "true"],
                "#09BA51",
                ["==", ["get", "active"], "false"],
                "#ffffff",
                "#ffffff",
              ],
              "line-width": 3,
              "line-dasharray": [
                "match",
                ["get", "user_portColor"],
                "#f7901e",
                ["literal", [0.2, 2]],
                ["literal", [1]],
              ],
            },
          },
          {
            id: "gl-draw-line-active",
            type: "line",
            filter: [
              "all",
              ["==", "$type", "LineString"],
              ["==", "active", "true"],
            ],
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
            paint: {
              "line-color": [
                "case",
                ["==", ["get", "user_portColor"], "#f7901e"],
                "#f7901e",
                ["==", ["get", "active"], "true"],
                "#09BA51",
                ["==", ["get", "active"], "false"],
                "#ffffff",
                "#ffffff",
              ],
              "line-width": 3,
              "line-dasharray": [
                "match",
                ["get", "user_portColor"],
                "#f7901e",
                ["literal", [0.2, 2]],
                ["literal", [1]],
              ],
            },
          },
        ],
      });
      newMap.addControl(newDraw);
      dispatch({ type: MapActions.SET_DRAW, value: newDraw });
    },
    [dispatch]
  );

  const initializeDrawCreate = useCallback(async () => {
    map.on("draw.create", (event) => measureMethod(event, draw, map));
    map.on("draw.update", (event) => measureMethod(event, draw, map));
    map.on("draw.delete", (event) => measureMethod(event, draw, map));
  }, [map, draw, measureMethod]);

  useEffect(() => {
    if (!draw) return;
    if (!measureMode) return;
    if (measureMode !== "") {
      initializeDrawCreate();
    }
  }, [measureMode, initializeDrawCreate, draw]);

  const initializeGeocoder = (newMap) => {
    const geocoderContainer = document.getElementsByClassName(
      "mapboxgl-ctrl-geocoder"
    );

    if (geocoderContainer.length) return;

    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      marker: false,
      mapboxgl,
    });
    document.getElementById("geocoder").appendChild(geocoder.onAdd(newMap));
  };

  const initializeMap = useCallback(() => {
    if (map) return;

    const mapBounds = getMapBounds();
    const newMap = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/satellite-v9",
      attributionControl: false,
      transformRequest: (url, resourceType) => {
        if (
          resourceType === "Tile" &&
          url.startsWith(process.env.REACT_APP_DATA_FARMING_API_ORIGIN)
        ) {
          return {
            url,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };
        }

        return null;
      },
      zoom: mapBounds.zoom,
      center: mapBounds.center,
      preserveDrawingBuffer: true,
    });
    newMap.on("load", () => {
      initializeControls(newMap);
      initializeGeocoder(newMap);
    });

    dispatch({ type: MapActions.SET_MAP, value: newMap });
  }, [dispatch, getMapBounds, map, token, initializeControls]);

  const initializeFields = useCallback(() => {
    const transformedFields = transformDataToMapboxSource(fields);
    transformedFields.forEach((transformedField) => {
      const { id } = transformedField.data;

      if (!map.getSource(`source-${id}`)) {
        map.addSource(`source-${id}`, transformedField);
      }

      if (!map.getLayer(`outline-${id}`)) {
        map.addLayer({
          id: `outline-${id}`,
          type: "line",
          source: `source-${id}`,
          layout: {
            "line-cap": "round",
            "line-join": "round",
          },
          paint: {
            "line-color": [
              "case",
              ["boolean", ["feature-state", "active"], false],
              "#09BA51",
              "#FFFFFF",
            ],
            "line-width": [
              "case",
              ["boolean", ["feature-state", "active"], false],
              2,
              3,
            ],
            "line-opacity": 0.7,
          },
        });
      }

      if (!map.getLayer(`fill-${id}`)) {
        map.addLayer({
          id: `fill-${id}`,
          type: "fill",
          source: `source-${id}`,
          paint: {
            "fill-color": [
              "case",
              ["boolean", ["feature-state", "active"], false],
              "#09BA51",
              "#FFFFFF",
            ],
            "fill-opacity": [
              "case",
              ["boolean", ["feature-state", "tiled"], false],
              0,
              ["boolean", ["feature-state", "active"], false],
              0.2,
              ["boolean", ["feature-state", "hover"], false],
              0.5,
              0,
            ],
          },
        });
      }
      map.on("mousemove", `fill-${id}`, (e) => {
        if (!e.features.length) return;

        map.setFeatureState({ source: `source-${id}`, id }, { hover: true });
      });
      map.on("mouseleave", `fill-${id}`, () => {
        map.setFeatureState({ source: `source-${id}`, id }, { hover: false });
      });
      map.on("click", `fill-${id}`, () => {
        const areaTool = document.getElementById("measure-area-tool");
        const lineTool = document.getElementById("measure-line-tool");
        const currentMode = localStorage.getItem("isMeasuring");
        if (
          !lineTool?.classList?.contains("active") &&
          !areaTool?.classList?.contains("active") &&
          !currentMode
        ) {
          const multiSelectEnabled = profile.feature_flags.find(
            (item) =>
              item.feature_flag === "field:multi_select" &&
              item.enabled === true
          );
          dispatch({
            type: MapActions.SET_SELECTED_FIELDS,
            value: { fieldId: id, multiSelectEnabled },
          });

          // eslint-disable-next-line no-param-reassign
          lastSelectedSourceRef.current = "map";
        }
      });

      map.on("touchstart", `fill-${id}`, () => {
        const areaTool = document.getElementById("measure-area-tool");
        const lineTool = document.getElementById("measure-line-tool");
        const currentMode = localStorage.getItem("isMeasuring");
        if (
          !lineTool?.classList.contains("active") &&
          !areaTool?.classList.contains("active") &&
          !currentMode
        ) {
          const multiSelectEnabled = profile.feature_flags.find(
            (item) =>
              item.feature_flag === "field:multi_select" &&
              item.enabled === true
          );
          dispatch({
            type: MapActions.SET_SELECTED_FIELDS,
            value: { fieldId: id, multiSelectEnabled },
          });
        }
      });
    });
  }, [dispatch, fields, lastSelectedSourceRef, map, profile?.feature_flags]);

  const resetFieldHighlight = useCallback(() => {
    fields.map((field) => {
      const source = map.getSource(`source-${field.id}`);

      if (source) {
        map.setFeatureState(
          { source: `source-${field.id}`, id: field.id },
          { active: false, tiled: false }
        );
      }

      return null;
    });
  }, [fields, map]);

  const highlighSelectedFields = useCallback(() => {
    resetFieldHighlight();

    if (mode === "manage") return;

    selectedFields.map((selectedField) => {
      map.setFeatureState(
        { source: `source-${selectedField}`, id: selectedField },
        { active: true }
      );

      return null;
    });
  }, [map, mode, resetFieldHighlight, selectedFields]);

  const zoomSelectedFields = useCallback(() => {
    if (!fields.length) return;

    if (lastSelectedSourceRef.current === "map") return;

    const padding = {
      left: 0,
      top: 100,
      right: drawerOpen ? 360 : 0,
      bottom: 150,
    };

    if (selectedFields.length) {
      const newSelectedFields = selectedFields.map((id) =>
        fields.find((field) => field.id === id)
      );
      const features = featureCollection(newSelectedFields);
      map.fitBounds(envelope(features).bbox, {
        padding,
      });

      return;
    }

    const features = featureCollection(fields);
    map.fitBounds(envelope(features).bbox, { padding });
  }, [drawerOpen, fields, lastSelectedSourceRef, map, selectedFields]);

  const removeExistingRaster = useCallback(() => {
    if (!map) return;

    [...Array(rasterTiles).keys()].map((key) => {
      const existingSource = map.getSource(`raster-source-${key}`);
      const existingLayer = map.getLayer(`raster-layer-${key}`);

      if (existingLayer) {
        map.removeLayer(`raster-layer-${key}`);
      }

      if (existingSource) {
        map.removeSource(`raster-source-${key}`);
      }

      return null;
    });

    if (!selectedFields.length) {
      dispatch({ type: MapActions.SET_RASTER_TILES, value: 0 });
    }
  }, [dispatch, map, rasterTiles, selectedFields]);

  const loadRaster = useCallback(() => {
    if (!isToggled) {
      dispatch({
        type: MapActions.SET_RASTER_LOADING,
        value: true,
      });
    }

    const selectedCatalog = catalog.find(
      (item) => item.date === selectedDate || item.start_date === selectedDate
    );
    const selectedItem = selectedCatalog.items.find(
      (item) => item.algorithm === selectedAlgorithm
    );

    selectedItem.tile_urls.map((item, index) => {
      map.addSource(`raster-source-${index}`, {
        tiles: [item],
        type: "raster",
      });
      map.addLayer({
        source: `raster-source-${index}`,
        type: "raster",
        id: `raster-layer-${index}`,
      });

      return null;
    });

    setTimeout(() => {
      dispatch({
        type: MapActions.SET_RASTER_LOADING,
        value: false,
      });
    }, 10000);

    map.on("sourcedata", (e) => {
      if (
        e.isSourceLoaded &&
        e.tile &&
        ["loaded", "errored"].includes(e.tile.state)
      ) {
        dispatch({
          type: MapActions.SET_RASTER_LOADING,
          value: false,
        });
      }
    });

    dispatch({
      type: MapActions.SET_RASTER_TILES,
      value: selectedItem.tile_urls.length,
    });
  }, [catalog, dispatch, map, selectedAlgorithm, selectedDate, isToggled]);

  const updateRasterPosition = useCallback(() => {
    selectedFields.map((selectedField) => {
      map.setFeatureState(
        { source: `source-${selectedField}`, id: selectedField },
        { tiled: true }
      );

      [...Array(rasterTiles).keys()].map((key) => {
        map.moveLayer(`raster-layer-${key}`, `outline-${selectedField}`);

        return null;
      });

      const drawLayer = map
        .getStyle()
        .layers?.filter((layer) => layer?.id?.includes("gl-draw-"));
      [...Array(rasterTiles).keys()].map(() => {
        drawLayer.forEach((element) => {
          map.moveLayer(element?.id);
        });

        return null;
      });

      return null;
    });
  }, [map, rasterTiles, selectedFields]);

  useEffect(() => {
    initializeMap();
  }, [initializeMap]);

  useEffect(() => {
    if (!map) return;

    map.on("load", () => {
      initializeFields();
    });
    map.on("mousemove", (e) => {
      document.getElementById(
        "info"
      ).innerHTML = `Lat: ${"  "} ${JSON.stringify(
        e.lngLat.lat.toFixed(7)
      )} Long: ${JSON.stringify(e.lngLat.lng.toFixed(7))}`;
    });
  }, [initializeFields, dateNow, map]);

  useEffect(() => {
    if (map && selectedFields) {
      highlighSelectedFields();
      zoomSelectedFields();
    }
  }, [highlighSelectedFields, map, selectedFields, zoomSelectedFields]);

  useEffect(() => {
    removeExistingRaster();

    if (selectedDate && toggleAlgorithm) {
      loadRaster();
      updateRasterPosition();
    }
  }, [
    loadRaster,
    removeExistingRaster,
    selectedDate,
    updateRasterPosition,
    toggleAlgorithm,
  ]);

  useEffect(() => {
    if (mode === "zoning" && zoneLayerTransparency) {
      setZoneLayerTransparencyLabel(zoneLayerTransparency);
    }
  }, [mode, zoneLayerTransparency]);

  const handleToggleLayer = () => {
    setToggleAlgorithm(!toggleAlgorithm);
    dispatch({ type: MapActions.SET_TOGGLE_CALCULATIONS, value: true });
  };

  return (
    <div id="mapbox" ref={mapContainer} className="mapping-tool">
      <div
        id="info"
        className="mapboxgl-coordinates-bottom-right with-coordinates"
        style={exportMode ? { display: "none" } : {}}
      />
      {mode === "zoning" && (
        <div className="zoning-cart">
          <button type="button" onClick={() => setOpenCart(!openCart)}>
            <ShoppingCart size={24} color="#fff" />
          </button>
        </div>
      )}
      {mode === "zoning" && (
        <div className="zoning-transparency">
          <div className="algorithm-toggle">
            <CheckBox
              onChange={() => handleToggleLayer()}
              defaultChecked={selectedAlgorithm}
            />
            <p className="label">{selectedAlgorithm}</p>
          </div>
          Transparency
          <ReactSlider
            className="slider"
            onChange={(value) => setZoneLayerTransparencyLabel(1 - value / 100)}
            onAfterChange={() =>
              dispatch({
                type: MapActions.SET_ZONE_LAYER_TRANSPARENCY,
                value: zoneLayerTransparencyLabel,
              })
            }
            renderThumb={(props) => <div {...props} />}
            defaultValue={Math.round((1 - zoneLayerTransparency) * 100)}
          />
          <div className="value">
            {Math.round((1 - zoneLayerTransparencyLabel) * 100)}%
          </div>
        </div>
      )}
      {openCart && (
        <CartModal isOpen={openCart} onClose={() => setOpenCart(false)} />
      )}
      {selectedLayer === "rice_harvest_prediction" && selectedFields.length && (
        <HarvestMoisture />
      )}
    </div>
  );
};

Map.defaultProps = {
  lastSelectedSourceRef: { current: null },
};

Map.propTypes = {
  lastSelectedSourceRef: PropTypes.shape({
    current: PropTypes.string,
  }),
};

export default Map;
